import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadWritingCorrectionLocally } from "@/utils/writingCorrectionCache";
import { resolveWritingScore, resolveWritingMaxScore, getWritingNclcFromScore } from "@/utils/writingResultUtils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Calendar, 
  Clock, 
  Filter, 
  Award, 
  AlertCircle, 
  Flame, 
  BookOpen, 
  Headphones, 
  PenTool, 
  Trash2, 
  CheckSquare, 
  Square, 
  Sparkles,
  ArrowLeft,
  X,
  Info,
  Loader2,
  CheckCircle2, 
  ChevronRight, 
  Search, 
  MoreHorizontal, 
  FileText, 
  Download, 
  Share2, 
  Target, 
  Trophy
} from "lucide-react";
import { historyService, type HistoryItem } from "@/services/historyService";
import { isSupabaseConfigured } from "@/lib/supabaseClient";
import { normalizeReadingSection, getReadingLabelByKey } from "@/data/tefStructure";
import { formatPercentage, formatCompactScore } from "@/lib/utils";
import { ContextModal } from "@/components/ui/ContextModal";
import { getCompetencyLabel, getTefLevelByScore } from "@/data/tefScoreScale";
import { writingCorrectionHistoryService } from "@/services/writingCorrectionHistoryService";
import WritingCorrectionReport from "@/components/writing/WritingCorrectionReport";
import { isWritingV2Result, isLegacyWritingResult } from "@/utils/writingV2Helpers";
import { useStudyTimeTracker } from "@/hooks/useStudyTimeTracker";


// Utility to parse percentage from scores
function parseScorePercentage(score: any, percentageField?: number): number {
  if (typeof percentageField === "number") return percentageField;
  if (typeof score === "number") return score;
  if (!score || typeof score !== "string") return 0;
  
  if (score.includes("%")) {
    const match = score.match(/(\d+(?:\.\d+)?)\s*%/);
    if (match) return parseFloat(match[1]);
  }
  
  const parts = score.split("/");
  if (parts.length === 2) {
    const num = parseFloat(parts[0]);
    const den = parseFloat(parts[1]);
    if (!isNaN(num) && !isNaN(den) && den > 0) {
      return Math.round((num / den) * 100);
    }
  }
  
  const parsed = parseFloat(score);
  return isNaN(parsed) ? 0 : parsed;
}

function isWritingV2HistoryRecord(record: any): boolean {
  return isWritingV2Result(record);
}

function isWritingHistoryRecord(record: any): boolean {
  return isWritingV2Result(record) || isLegacyWritingResult(record);
}

// Group and aggregate statistics
function getSectionPerformanceStats(historyItems: any[]) {
  const stats: {
    reading: Record<string, { attempts: number; sumPercent: number; bestPercent: number; lastPercent?: number; totalQuestions: number; correctAnswers: number; lastDate: string }>;
    listening: Record<string, { attempts: number; sumPercent: number; bestPercent: number; lastPercent?: number; totalQuestions: number; correctAnswers: number; lastDate: string }>;
    writing: Record<string, { attempts: number; sumWords: number; maxWords: number; lastWords?: number; lastDate: string }>;
  } = {
    reading: {},
    listening: {},
    writing: {}
  };

  historyItems.forEach(item => {
    const isFull = item.test_type === "complete" || item.testType === "complete" || item.mode === "full_exam" || item.competence === "Simulado Completo";
    
    // Process Reading
    const reading = isFull ? (item.result?.ce || item.sections?.reading) : (item.competence === "Reading" ? item : null);
    if (reading) {
      const subResults = reading.sectionResults || {};
      
      Object.keys(subResults).forEach(sectionName => {
        const sub = subResults[sectionName];
        const normKey = normalizeReadingSection(sectionName);
        const officialLabel = getReadingLabelByKey(normKey);

        if (!stats.reading[officialLabel]) {
          stats.reading[officialLabel] = { attempts: 0, sumPercent: 0, bestPercent: 0, totalQuestions: 0, correctAnswers: 0, lastDate: "" };
        }
        const s = stats.reading[officialLabel];
        s.attempts++;
        s.sumPercent += sub.percent;
        s.bestPercent = Math.max(s.bestPercent, sub.percent);
        if (!s.lastDate || new Date(item.date) > new Date(s.lastDate)) {
          s.lastPercent = sub.percent;
          s.lastDate = item.date;
        }
        s.totalQuestions += sub.total || 0;
        s.correctAnswers += sub.correct || 0;
      });
      
      if (!isFull && Object.keys(subResults).length === 0) {
        const officialLabel = "Todas";
        const pct = parseScorePercentage(item.score, item.percentage);
        if (!stats.reading[officialLabel]) {
          stats.reading[officialLabel] = { attempts: 0, sumPercent: 0, bestPercent: 0, totalQuestions: 0, correctAnswers: 0, lastDate: "" };
        }
        const s = stats.reading[officialLabel];
        s.attempts++;
        s.sumPercent += pct;
        s.bestPercent = Math.max(s.bestPercent, pct);
        if (!s.lastDate || new Date(item.date) > new Date(s.lastDate)) {
          s.lastPercent = pct;
          s.lastDate = item.date;
        }
      }
    }

    // Process Listening
    const listening = isFull ? (item.result?.co || item.sections?.listening) : (item.competence === "Listening" ? item : null);
    if (listening) {
      const subResults = listening.sectionResults || {};
      
      Object.keys(subResults).forEach(sectionName => {
        const sub = subResults[sectionName];
        if (!stats.listening[sectionName]) {
          stats.listening[sectionName] = { attempts: 0, sumPercent: 0, bestPercent: 0, totalQuestions: 0, correctAnswers: 0, lastDate: "" };
        }
        const s = stats.listening[sectionName];
        s.attempts++;
        s.sumPercent += sub.percent;
        s.bestPercent = Math.max(s.bestPercent, sub.percent);
        if (!s.lastDate || new Date(item.date) > new Date(s.lastDate)) {
          s.lastPercent = sub.percent;
          s.lastDate = item.date;
        }
        s.totalQuestions += sub.total || 0;
        s.correctAnswers += sub.correct || 0;
      });
      
      if (!isFull && Object.keys(subResults).length === 0) {
        const sectionName = item.selectedSection || "Todas";
        const pct = parseScorePercentage(item.score, item.percentage);
        if (!stats.listening[sectionName]) {
          stats.listening[sectionName] = { attempts: 0, sumPercent: 0, bestPercent: 0, totalQuestions: 0, correctAnswers: 0, lastDate: "" };
        }
        const s = stats.listening[sectionName];
        s.attempts++;
        s.sumPercent += pct;
        s.bestPercent = Math.max(s.bestPercent, pct);
        if (!s.lastDate || new Date(item.date) > new Date(s.lastDate)) {
          s.lastPercent = pct;
          s.lastDate = item.date;
        }
      }
    }

    // Process Writing
    const processSectionStat = (section: string, words: number, errors: number, date: string) => {
      const lower = String(section).trim().toLowerCase();
      let sectionName = "Section A";
      if (lower.includes("tâche 2") || lower.includes("tache 2") || lower.includes("task 2") || lower.includes("section b") || lower === "b") {
        sectionName = "Section B";
      }
      
      if (!stats.writing[sectionName]) {
        stats.writing[sectionName] = { 
          attempts: 0, 
          sumWords: 0, 
          maxWords: 0, 
          lastDate: "",
          sumErrors: 0,
          minErrors: 9999,
          lastErrors: 0
        } as any;
      }
      
      const s = stats.writing[sectionName] as any;
      s.attempts++;
      s.sumWords += words;
      s.maxWords = Math.max(s.maxWords, words);
      s.sumErrors = (s.sumErrors || 0) + errors;
      s.minErrors = Math.min(s.minErrors !== undefined ? s.minErrors : 9999, errors);
      
      if (!s.lastDate || new Date(date) > new Date(s.lastDate)) {
        s.lastWords = words;
        s.lastErrors = errors;
        s.lastDate = date;
      }
    };

    const hasWritingV2Parts = item.metadata?.writingTasks && item.metadata.writingTasks.length > 0;
    const hasWritingAnswers = item.answers?.sectionA || item.answers?.sectionB;
    
    if (hasWritingV2Parts) {
      item.metadata.writingTasks.forEach((task: any) => {
        const taskSec = task.section || "Section A";
        const taskWords = task.wordCount || task.words || 0;
        const taskErrors = task.correction?.errors?.length || task.errorCount || 0;
        processSectionStat(taskSec, taskWords, taskErrors, item.date);
      });
    } else if (hasWritingAnswers) {
      if (item.answers.sectionA) {
        const words = item.answers.sectionA.wordCount || item.answers.sectionA.words || 0;
        const errors = item.answers.sectionA.correction?.errors?.length || item.answers.sectionA.errorCount || 0;
        processSectionStat("Section A", words, errors, item.date);
      }
      if (item.answers.sectionB) {
        const words = item.answers.sectionB.wordCount || item.answers.sectionB.words || 0;
        const errors = item.answers.sectionB.correction?.errors?.length || item.answers.sectionB.errorCount || 0;
        processSectionStat("Section B", words, errors, item.date);
      }
    } else if (isWritingHistoryRecord(item) || item.competence === "Writing" || item.competence === "Expression Écrite (EE)") {
      const rawSec = item.selectedSection || item.section || "Section A";
      const words = item.words || item.metadata?.wordCount || 0;
      const errors = item.metadata?.errorCount || item.correction?.errors?.length || 0;
      processSectionStat(rawSec, words, errors, item.date);
    } else if (isFull) {
      const writing = item.result?.ee || item.sections?.writing || {};
      const words = writing.wordCount || writing.words || 0;
      if (words > 0) {
        processSectionStat("Section B", words, 0, item.date);
      }
    }
  });

  return stats;
}

// Map stats to officially sorted lists
function getMergedStats(aggregated: ReturnType<typeof getSectionPerformanceStats>) {
  const officialReading = [
    "Documents de la vie quotidienne",
    "Phrases et textes lacunaires",
    "Lecture rapide de textes et de graphiques",
    "Documents administratifs et professionnels",
    "Articles de presse"
  ];

  const officialListening = [
    "Messages vocaux",
    "Annonces publiques",
    "Dialogues / Reportages courts",
    "Discussions / Conférences longues"
  ];

  const officialWriting = [
    "Section A",
    "Section B"
  ];

  const mapStats = (officialList: string[], aggregatedComp: Record<string, any>, isWriting: boolean) => {
    const result = officialList.map(sec => {
      const s = aggregatedComp[sec] || { attempts: 0 };
      if (isWriting) {
        return {
          section: sec,
          attempts: s.attempts || 0,
          averageWordCount: s.attempts > 0 ? Math.round(s.sumWords / s.attempts) : 0,
          maxWordCount: s.maxWords || 0,
          lastWordCount: s.lastWords || 0,
          averageErrors: s.attempts > 0 ? Math.round(s.sumErrors / s.attempts) : 0,
          minErrors: s.minErrors === 9999 ? 0 : (s.minErrors || 0),
          lastErrors: s.lastErrors || 0,
          isV2: sec === "Section A" || sec === "Section B"
        };
      } else {
        return {
          section: sec,
          attempts: s.attempts || 0,
          averagePercentage: s.attempts > 0 ? Math.round(s.sumPercent / s.attempts) : 0,
          bestPercentage: s.bestPercent || 0,
          lastPercentage: s.lastPercent || 0
        };
      }
    });

    // Handle any custom categories outside the official list
    const list = Object.keys(aggregatedComp)
      .filter(k => !officialList.includes(k))
      .map(sec => {
        const s = aggregatedComp[sec];
        if (isWriting) {
          return {
            section: sec,
            attempts: s.attempts || 0,
            averageWordCount: s.attempts > 0 ? Math.round(s.sumWords / s.attempts) : 0,
            maxWordCount: s.maxWords || 0,
            lastWordCount: s.lastWords || 0,
            averageErrors: s.attempts > 0 ? Math.round(s.sumErrors / s.attempts) : 0,
            minErrors: s.minErrors === 9999 ? 0 : (s.minErrors || 0),
            lastErrors: s.lastErrors || 0,
            isV2: sec === "Section A" || sec === "Section B"
          };
        } else {
          return {
            section: sec,
            attempts: s.attempts || 0,
            averagePercentage: s.attempts > 0 ? Math.round(s.sumPercent / s.attempts) : 0,
            bestPercentage: s.bestPercent || 0,
            lastPercentage: s.lastPercent || 0
          };
        }
      });
    
    list.forEach(item => {
      result.push(item);
    });
    
    return result;
  };

  return {
    reading: mapStats(officialReading, aggregated.reading, false),
    listening: mapStats(officialListening, aggregated.listening, false),
    writing: mapStats(officialWriting, aggregated.writing, true)
  };
}

// Interpret displaying section column
const getSectionDisplay = (item: any, t?: any) => {
  if (!t) return item.selectedSection || item.section || "Todas";
  if (isWritingV2HistoryRecord(item)) {
    const sec = item.metadata?.section || item.section || "Section A";
    const mode = item.metadata?.testMode === "complete" 
      ? t("history.sectionDisplay.sectionV2", { section: sec }) 
      : t("history.sectionDisplay.sectionV2Individual", { section: sec });
    return mode;
  }
  if (item.mode === "full_exam") return t("history.sectionDisplay.fullSimulated");
  if (item.selectedSection) return item.selectedSection;
  if (item.section) return item.section;
  
  if (item.sectionResults) {
    const keys = Object.keys(item.sectionResults);
    if (keys.length === 1) return keys[0];
    if (keys.length > 1) return t("questionsBank.allSections");
  }
  
  if (item.mode === "complete" || !item.mode) return t("questionsBank.allSections");
  
  return t("history.sectionDisplay.noSection");
};

export function getCompetenceBadgeClasses(competence?: string): string {
  const value = (competence || "").toLowerCase().trim();

  if (
    (value.includes("écrite") && value.includes("ce")) || 
    value.includes("compréhension écrite") || 
    value === "reading" || 
    value === "ce"
  ) {
    return "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-50/80";
  }

  if (
    value.includes("orale") || 
    value.includes("co") || 
    value === "listening" || 
    value === "co"
  ) {
    return "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50/80";
  }

  if (
    value.includes("expression") || 
    value.includes("ee") || 
    value === "writing" || 
    value === "ee"
  ) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50/80";
  }

  if (
    value.includes("simulado") || 
    value.includes("complete") || 
    value.includes("completo")
  ) {
    return "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50/80";
  }

  return "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-50/80";
}

export default function History() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const querySelectedId = searchParams.get("selectedId");

  const [filter, setFilter] = useState<"all" | "Reading" | "Listening" | "Writing" | "Simulado Completo">("all");
  
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useStudyTimeTracker({
    activityType: "history_review",
    competency: "general",
    enabled: !isLoading
  });

  const [selectedHistoryIds, setSelectedHistoryIds] = useState<string[]>([]);
  
  const fetchMergedHistory = async () => {
    try {
      const std = await historyService.getTestHistory();
      console.log("[History] loaded records", { count: std.length });
      
      let merged = [];
      if (isSupabaseConfigured) {
        merged = std;
      } else {
        const wc = writingCorrectionHistoryService.getWritingCorrections().map(c => ({
          ...c,
          isWritingCorrection: true,
          id: c.id,
          date: c.savedAt || c.createdAt,
          competence: "Writing" as const,
          score: `${c.estimatedScore}/${c.maxScore}`,
          timeSpent: "N/A",
          status: "Corrigido por IA",
          words: c.wordCount,
          selectedSection: c.section
        }));
        merged = [...std, ...wc];
      }
      
      merged.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      console.log("[HISTORY_RENDER_MODE]", { 
        totalRecords: merged.length, 
        writingV2Records: merged.filter(isWritingV2HistoryRecord).length 
      });

      setHistoryList(merged);
    } catch (err) {
      console.error("Error loading merged history:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMergedHistory();
  }, []);
  
  // Modals / Toast state
  const [itemToDelete, setItemToDelete] = useState<any | null>(null);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [viewingCorrection, setViewingCorrection] = useState<any | null>(null);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    if (querySelectedId && historyList.length > 0) {
      const found = historyList.find(h => h.id === querySelectedId);
      if (found) {
        if (found.isWritingCorrection) {
          setViewingCorrection(found);
        } else {
          setSelectedItem(found);
        }
        setFilter("all"); // Ensure highlighted row is visible in filtered list
        
        // Wait briefly for render and scroll smoothly to row
        setTimeout(() => {
          const element = document.getElementById(`row-${querySelectedId}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 350);
      }
    }
  }, [querySelectedId, historyList]);

  // Subscribe to changes in history service
  useEffect(() => {
    const handleUpdate = () => {
      fetchMergedHistory();
      setSelectedHistoryIds([]); // Reset select items
    };
    window.addEventListener("historyUpdated", handleUpdate);
    return () => window.removeEventListener("historyUpdated", handleUpdate);
  }, []);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const filteredHistory = historyList.filter((h) => {
    if (filter === "all") return true;
    if (filter === "Simulado Completo") return h.mode === "full_exam";
    if (filter === "Writing") {
      return h.competence === "Writing" || h.competence === "Expression Écrite (EE)" || isWritingV2HistoryRecord(h);
    }
    return h.competence === filter;
  });

  console.log("[HISTORY_EE_V2_RECORDS_FOUND]", historyList
    .filter(isWritingV2HistoryRecord)
    .map(r => ({
      id: r.id,
      section: r.section,
      metadataSection: r.metadata?.section,
      wordCount: r.metadata?.wordCount,
      errorCount: r.metadata?.errorCount,
      hasTask: Boolean(r.metadata?.writingTask),
      hasCorrection: Boolean(r.metadata?.writingCorrection),
    }))
  );

  // Calculate high-quality analytics dynamically
  const rawStats = getSectionPerformanceStats(historyList);
  const stats = getMergedStats(rawStats);

  // 1. General MCQ Average
  const mcqItems = historyList.filter(h => h.competence === "Reading" || h.competence === "Listening" || h.mode === "full_exam");
  let mcqSum = 0;
  let mcqCount = 0;
  mcqItems.forEach(h => {
    if (h.mode === "full_exam") {
      if (h.sections?.reading?.percent !== undefined) {
        mcqSum += h.sections.reading.percent;
        mcqCount++;
      }
      if (h.sections?.listening?.percent !== undefined) {
        mcqSum += h.sections.listening.percent;
        mcqCount++;
      }
    } else {
      const p = parseScorePercentage(h.score, h.percentage);
      if (p > 0) {
        mcqSum += p;
        mcqCount++;
      }
    }
  });
  const overallAvg = mcqCount > 0 ? formatPercentage(mcqSum / mcqCount) : "0,0%";

  // 2. Total Proof timer Spent
  let totalMinutes = 0;
  historyList.forEach(h => {
    if (h.timeSpent && typeof h.timeSpent === "string") {
      const parts = h.timeSpent.split(":");
      if (parts.length >= 2) {
        const mins = parseInt(parts[0]);
        const secs = parseInt(parts[1]);
        if (!isNaN(mins)) {
          totalMinutes += mins + (secs / 60);
        }
      }
    } else if ((h as any).timeUsed) {
      totalMinutes += (h as any).timeUsed / 60;
    }
  });
  const totalMinsRounded = Math.round(totalMinutes);

  // 3. Generated essays counting
  const writingCount = historyList.filter(h => 
    h.competence === "Writing" || 
    h.competence === "Expression Écrite (EE)" ||
    h.sections?.writing || 
    isWritingV2HistoryRecord(h)
  ).length;

  // Compute Highlights
  let bestSection = t("history.noDataYet");
  let bestVal = -1;
  let worstSection = t("history.noDataYet");
  let worstVal = 101;
  let mostTrainedSection = t("history.noDataYet");
  let mostTrainedVal = 0;

  // Best / Worst in MCQ sub-sections
  const mcqSections = [...stats.reading, ...stats.listening];
  mcqSections.forEach(s => {
    if (s.attempts > 0) {
      if (s.averagePercentage > bestVal) {
        bestVal = s.averagePercentage;
        bestSection = `${s.section} — ${formatPercentage(s.averagePercentage)}`;
      }
      if (s.averagePercentage < worstVal) {
        worstVal = s.averagePercentage;
        worstSection = `${s.section} — ${formatPercentage(s.averagePercentage)}`;
      }
    }
  });

  // Most trained overall
  const allSections = [...stats.reading, ...stats.listening, ...stats.writing];
  allSections.forEach(s => {
    if (s.attempts > mostTrainedVal) {
      mostTrainedVal = s.attempts;
      mostTrainedSection = `${s.section} — ${t("history.attemptsCount", { count: s.attempts })}`;
    }
  });

  // Selection Checkboxes logic
  const isAllVisibleSelected = filteredHistory.length > 0 && filteredHistory.every(item => selectedHistoryIds.includes(item.id));
  const isSomeVisibleSelected = filteredHistory.length > 0 && filteredHistory.some(item => selectedHistoryIds.includes(item.id)) && !isAllVisibleSelected;

  const toggleAllVisible = () => {
    if (isAllVisibleSelected) {
      const visibleIds = new Set(filteredHistory.map(h => h.id));
      setSelectedHistoryIds(prev => prev.filter(id => !visibleIds.has(id)));
    } else {
      const visibleIds = filteredHistory.map(h => h.id);
      setSelectedHistoryIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedHistoryIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Delete Handlers
  const handleConfirmSingleDelete = () => {
    if (!itemToDelete) return;
    if (itemToDelete.isWritingCorrection) {
      writingCorrectionHistoryService.deleteWritingCorrection(itemToDelete.id);
    } else {
      historyService.deleteHistoryItem(itemToDelete.id);
    }
    showToast(t("history.deleteSuccess"));
    setItemToDelete(null);
  };

  const handleConfirmBatchDelete = () => {
    if (selectedHistoryIds.length === 0) return;
    const count = selectedHistoryIds.length;
    
    const stdIds = selectedHistoryIds.filter(id => !id.startsWith("corr_"));
    const wcIds = selectedHistoryIds.filter(id => id.startsWith("corr_"));
    
    if (stdIds.length > 0) {
      historyService.deleteHistoryItems(stdIds);
    }
    wcIds.forEach(id => {
      writingCorrectionHistoryService.deleteWritingCorrection(id);
    });
    
    showToast(t("history.batchDeleteSuccess", { count }));
    setIsBatchDeleting(false);
  };

  // RENDER LOADING STATE
  if (isLoading) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500 pb-12 max-w-7xl mx-auto px-4 mt-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t("history.title")}</h1>
          <p className="text-gray-500 mt-1">{t("history.loading")}</p>
        </div>
        
        <div className="py-24 text-center text-gray-400 flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-100 shadow-sm animate-pulse">
          <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
          <h4 className="font-bold text-gray-700 text-base">{t("history.loadingTitle")}</h4>
          <p className="text-xs text-gray-500 mt-1">{t("history.loadingDesc")}</p>
        </div>
      </div>
    );
  }

  // RENDER EMPTY STATE
  if (historyList.length === 0) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500 pb-12">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t("history.title")}</h1>
          <p className="text-gray-500 mt-1">{t("history.subtitle")}</p>
        </div>
        
        {/* Empty state container */}
        <div className="py-24 text-center text-gray-400 flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-100 shadow-sm">
          <Calendar className="w-16 h-16 mb-4 stroke-[1.2] text-gray-300" />
          <h4 className="font-bold text-gray-700 text-base">{t("history.noHistoryTitle")}</h4>
          <p className="text-xs text-gray-500 mt-1 max-w-sm leading-relaxed">
            {t("history.noHistoryDesc")}
          </p>
          <Button asChild className="mt-6 bg-primary hover:bg-primary/95 text-white rounded-xl shadow-md px-6 font-bold py-5">
            <Link to="/test/start">{t("history.startTestButton")}</Link>
          </Button>
        </div>

        {/* Toast Notifications */}
        {toastMessage && (
          <div className="fixed top-6 right-6 z-50 bg-gray-900 text-white font-bold text-xs py-3.5 px-6 rounded-2xl shadow-xl flex items-center gap-2 animate-in slide-in-from-top duration-300">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-ping" />
            <span>{toastMessage}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 rounded-lg gap-1.5 font-semibold text-xs self-start"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("common.back")}
      </Button>

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t("history.title")}</h1>
        <p className="text-gray-500 mt-1">{t("history.subtitle")}</p>
      </div>

      {/* Historical summary metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-gray-500 font-semibold uppercase tracking-wider">{t("history.averageScore")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-800">{overallAvg}</div>
            <p className="text-xs text-gray-400 mt-1">{t("history.averageScoreDesc")}</p>
          </CardContent>
        </Card>
        
        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-gray-500 font-semibold uppercase tracking-wider">{t("history.totalTime")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-800">{totalMinsRounded} min</div>
            <p className="text-xs text-gray-400 mt-1">{t("history.totalTimeDesc")}</p>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-gray-500 font-semibold uppercase tracking-wider">{t("history.essaysGenerated")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-800">{writingCount} {writingCount === 1 ? t("history.essayCount_one") : t("history.essayCount_other")}</div>
            <p className="text-xs text-gray-400 mt-1">{t("history.essaysGeneratedDesc")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Desempenho por subseção */}
      <div className="space-y-6 bg-gray-50/50 p-6 rounded-2xl border border-gray-100/80">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("history.performanceSubsections")}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{t("history.performanceSubsectionsDesc")}</p>
        </div>

        {/* Highlights Row */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-emerald-50 flex items-start gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
              <Award className="w-4.5 h-4.5" />
            </div>
            <div>
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.bestComp")}</span>
              <span className="text-xs font-bold text-gray-800 mt-1 block leading-tight">{bestSection}</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-amber-50 flex items-start gap-3">
            <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
              <AlertCircle className="w-4.5 h-4.5" />
            </div>
            <div>
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.worstComp")}</span>
              <span className="text-xs font-bold text-gray-800 mt-1 block leading-tight">{worstSection}</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-purple-50 flex items-start gap-3">
            <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
              <Flame className="w-4.5 h-4.5" />
            </div>
            <div>
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.mostTrained")}</span>
              <span className="text-xs font-bold text-gray-800 mt-1 block leading-tight">{mostTrainedSection}</span>
            </div>
          </div>
        </div>

        {/* Competency cards grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Reading Card */}
          {(filter === "all" || filter === "Reading" || filter === "Simulado Completo") && (
            <Card className="border-none shadow-sm bg-white overflow-hidden flex flex-col">
              <CardHeader className="bg-gradient-to-r from-purple-500/5 to-purple-600/5 pb-4 border-b border-purple-100/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    <div>
                      <CardTitle className="text-sm font-bold text-purple-950">{t("history.readingTitle")}</CardTitle>
                      <CardDescription className="text-[10px] text-purple-600 font-medium">{t("history.readingSubtitle")}</CardDescription>
                    </div>
                  </div>
                  <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 border-none font-bold text-[10px] px-2 py-0.5">
                    {t("history.attemptsCount", { count: stats.reading.reduce((acc, s) => acc + s.attempts, 0) })}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-5 space-y-4 flex-1">
                {stats.reading.map((sec) => (
                  <div key={sec.section} className="space-y-1 group">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-gray-700 group-hover:text-purple-700 transition-colors">{sec.section}</span>
                      <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                        {t("history.attemptsCount", { count: sec.attempts })}
                      </span>
                    </div>
                    
                    {sec.attempts > 0 ? (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-[10px] text-gray-400">
                          <span>{t("history.average")}: <span className="text-purple-600 font-bold font-mono">{formatPercentage(sec.averagePercentage)}</span></span>
                          <div className="flex gap-1.5 font-mono">
                            <span>{t("history.best")}: <span className="font-semibold text-gray-600">{formatPercentage(sec.bestPercentage)}</span></span>
                            <span>|</span>
                            <span>{t("history.last")}: <span className="font-semibold text-gray-600">{formatPercentage(sec.lastPercentage)}</span></span>
                          </div>
                        </div>
                        <div className="relative w-full h-1 bg-purple-100/50 rounded-full overflow-hidden">
                          <div className="h-full bg-purple-600 rounded-full transition-all duration-500" style={{ width: `${sec.averagePercentage}%` }} />
                        </div>
                      </div>
                    ) : (
                      <div className="text-[9px] text-gray-400 italic">{t("history.noTraining")}</div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Listening Card */}
          {(filter === "all" || filter === "Listening" || filter === "Simulado Completo") && (
            <Card className="border-none shadow-sm bg-white overflow-hidden flex flex-col">
              <CardHeader className="bg-gradient-to-r from-blue-500/5 to-blue-600/5 pb-4 border-b border-blue-100/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Headphones className="w-5 h-5 text-blue-600" />
                    <div>
                      <CardTitle className="text-sm font-bold text-blue-950">{t("history.listeningTitle")}</CardTitle>
                      <CardDescription className="text-[10px] text-blue-600 font-medium">{t("history.listeningSubtitle")}</CardDescription>
                    </div>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none font-bold text-[10px] px-2 py-0.5">
                    {t("history.attemptsCount", { count: stats.listening.reduce((acc, s) => acc + s.attempts, 0) })}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-5 space-y-4 flex-1">
                {stats.listening.map((sec) => (
                  <div key={sec.section} className="space-y-1 group">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-gray-700 group-hover:text-blue-700 transition-colors">{sec.section}</span>
                      <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                        {t("history.attemptsCount", { count: sec.attempts })}
                      </span>
                    </div>
                    
                    {sec.attempts > 0 ? (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-[10px] text-gray-400">
                          <span>{t("history.average")}: <span className="text-blue-600 font-bold font-mono">{formatPercentage(sec.averagePercentage)}</span></span>
                          <div className="flex gap-1.5 font-mono">
                            <span>{t("history.best")}: <span className="font-semibold text-gray-600">{formatPercentage(sec.bestPercentage)}</span></span>
                            <span>|</span>
                            <span>{t("history.last")}: <span className="font-semibold text-gray-600">{formatPercentage(sec.lastPercentage)}</span></span>
                          </div>
                        </div>
                        <div className="relative w-full h-1 bg-blue-100/50 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${sec.averagePercentage}%` }} />
                        </div>
                      </div>
                    ) : (
                      <div className="text-[9px] text-gray-400 italic">{t("history.noTraining")}</div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Writing Card */}
          {(filter === "all" || filter === "Writing" || filter === "Simulado Completo") && (
            <Card className="border-none shadow-sm bg-white overflow-hidden flex flex-col">
              <CardHeader className="bg-gradient-to-r from-emerald-500/5 to-emerald-600/5 pb-4 border-b border-emerald-100/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PenTool className="w-5 h-5 text-emerald-600" />
                    <div>
                      <CardTitle className="text-sm font-bold text-emerald-950">{t("history.writingTitle")}</CardTitle>
                      <CardDescription className="text-[10px] text-emerald-600 font-medium">{t("history.writingSubtitle")}</CardDescription>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none font-bold text-[10px] px-2 py-0.5">
                    {stats.writing.reduce((acc, s) => acc + s.attempts, 0)} {stats.writing.reduce((acc, s) => acc + s.attempts, 0) === 1 ? t("history.essayCount_one") : t("history.essayCount_other")}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-5 space-y-4 flex-1">
                {stats.writing.map((sec: any) => {
                  const isV2 = sec.isV2 || sec.section === "Section A" || sec.section === "Section B";
                  
                  if (isV2) {
                    return (
                      <div key={sec.section} className="space-y-1 group">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-gray-700 group-hover:text-emerald-700 transition-colors">
                            {sec.section === "Section A" ? t("writingErrors.sectionA", "Section A") : sec.section === "Section B" ? t("writingErrors.sectionB", "Section B") : sec.section}
                          </span>
                          <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                            {sec.attempts} {sec.attempts === 1 ? t("history.pedagogicalCorrection").toLowerCase() : t("history.pedagogicalCorrection").toLowerCase() + "s"}
                          </span>
                        </div>
                        
                        {sec.attempts > 0 ? (
                          <div className="space-y-1 pt-1">
                            <div className="flex flex-col gap-1 text-[10px] text-gray-500">
                              <div className="flex justify-between">
                                <span>{t("history.avgWords")}: <span className="text-emerald-600 font-bold">{sec.averageWordCount}</span></span>
                                <span>{t("history.avgErrors")}: <span className="text-purple-600 font-bold">{sec.averageErrors}</span></span>
                              </div>
                              <div className="flex justify-between text-[9px] text-gray-400">
                                <span>{t("history.maxWords")}: {sec.maxWordCount}</span>
                                <span>{t("history.minErrors")}: {sec.minErrors} · {t("history.last")}: {sec.lastErrors}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-[9px] text-gray-400 italic">{t("history.noCorrections")}</div>
                        )}
                      </div>
                    );
                  }

                  const targetWords = sec.section === "Tâche 1" ? 120 : 200;
                  const wordPercent = Math.min(100, (sec.averageWordCount / targetWords) * 100);
                  return (
                     <div key={sec.section} className="space-y-1 group">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-gray-700 group-hover:text-emerald-700 transition-colors">{sec.section}</span>
                        <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                          {sec.attempts} {sec.attempts === 1 ? t("history.essayCount_one") : t("history.essayCount_other")}
                        </span>
                      </div>
                      
                      {sec.attempts > 0 ? (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-[10px] text-gray-400">
                            <span>{t("history.average")}: <span className="text-emerald-600 font-bold font-mono">{sec.averageWordCount} {t("history.wordsShort")}</span></span>
                            <div className="flex gap-1.5 font-mono">
                              <span>{t("history.maxWords")}: <span className="font-semibold text-gray-600">{sec.maxWordCount} {t("history.wordsShort")}</span></span>
                              <span>|</span>
                              <span>{t("history.last")}: <span className="font-semibold text-gray-600">{sec.lastWordCount} {t("history.wordsShort")}</span></span>
                            </div>
                          </div>
                          <div className="relative w-full h-1 bg-emerald-100/50 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-600 rounded-full transition-all duration-500" style={{ width: `${wordPercent}%` }} />
                          </div>
                        </div>
                      ) : (
                        <div className="text-[9px] text-gray-400 italic">{t("history.noTraining")}</div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Filters row bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Filter className="w-4 h-4 text-gray-400" />
          <span>{t("history.filterCompetency")}</span>
        </div>
        <div className="flex gap-2 w-full sm:w-auto overflow-x-auto">
          {["all", "Reading", "Listening", "Writing", "Simulado Completo"].map((category) => (
            <Button
              key={category}
              variant={filter === category ? "default" : "outline"}
              onClick={() => setFilter(category as any)}
              className={`rounded-lg text-xs py-1 px-3 whitespace-nowrap ${filter === category ? "bg-primary hover:bg-primary/95 text-white" : "bg-white text-gray-600 border-gray-100 hover:bg-gray-50"}`}
            >
              {category === "all" ? t("history.all") : category === "Simulado Completo" ? t("history.sectionDisplay.fullSimulated") : getCompetencyLabel(category)}
            </Button>
          ))}
        </div>
      </div>

      {/* Action Bar for Batch Operation */}
      {selectedHistoryIds.length > 0 && (
        <div className="bg-red-50/70 border border-red-200/50 rounded-xl p-4 flex items-center justify-between animate-in slide-in-from-top duration-300">
          <div className="flex items-center gap-2 text-sm text-red-800 font-bold">
            <AlertCircle className="w-4.5 h-4.5 text-red-600" />
            <span>{selectedHistoryIds.length} {selectedHistoryIds.length === 1 ? t("questionsBank.questionSelected") : t("questionsBank.questionsSelected")}</span>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setSelectedHistoryIds([])}
              className="rounded-lg text-xs bg-white text-gray-700 border-gray-200 font-semibold"
            >
              {t("history.clearSelection")}
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => setIsBatchDeleting(true)}
              className="rounded-lg text-xs font-bold"
            >
              {t("history.deleteSelectedCount", { count: selectedHistoryIds.length })}
            </Button>
          </div>
        </div>
      )}

      {/* Historical logs table list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow>
              <TableHead className="w-[50px]">
                <button 
                  onClick={toggleAllVisible}
                  className="flex items-center justify-center p-1 text-gray-400 hover:text-primary transition-colors focus:outline-none"
                >
                  {isAllVisibleSelected ? (
                    <CheckSquare className="w-4.5 h-4.5 text-primary" />
                  ) : isSomeVisibleSelected ? (
                    <div className="w-4.5 h-4.5 border border-primary bg-primary/20 flex items-center justify-center rounded">
                      <div className="w-2 h-0.5 bg-primary" />
                    </div>
                  ) : (
                    <Square className="w-4.5 h-4.5 text-gray-300" />
                  )}
                </button>
              </TableHead>
              <TableHead className="font-semibold text-gray-600">{t("history.dateAndHour")}</TableHead>
              <TableHead className="font-semibold text-gray-600">{t("questionsBank.competence")}</TableHead>
              <TableHead className="font-semibold text-gray-600">{t("history.sectionTask")}</TableHead>
              <TableHead className="font-semibold text-gray-600">{t("history.scoreStatus")}</TableHead>
              <TableHead className="font-semibold text-gray-600">{t("history.timeSpentCol")}</TableHead>
              <TableHead className="font-semibold text-gray-600 text-right pr-6">{t("questionsBank.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredHistory.map((item) => {
              const isFullExam = item.mode === "full_exam";
              const isSelected = selectedHistoryIds.includes(item.id);
              const isQueryHighlighted = item.id === querySelectedId;
              const cellHighlightClass = isQueryHighlighted ? "border-t-2 border-b-2 border-purple-500 first:border-l-2 last:border-r-2" : "";

              return (
                <TableRow 
                  key={item.id} 
                  id={`row-${item.id}`}
                  className={`transition-all duration-300 ${
                    isQueryHighlighted 
                      ? "bg-purple-50/60 hover:bg-purple-100/80 shadow-sm" 
                      : isSelected 
                        ? "bg-primary/5 hover:bg-primary/5" 
                        : "hover:bg-gray-50/50"
                  }`}
                >
                  <TableCell className={cellHighlightClass}>
                    <button 
                      onClick={() => toggleSelect(item.id)}
                      className="flex items-center justify-center p-1 text-gray-400 hover:text-primary transition-colors focus:outline-none"
                    >
                      {isSelected ? (
                        <CheckSquare className="w-4.5 h-4.5 text-primary" />
                      ) : (
                        <Square className="w-4.5 h-4.5 text-gray-300" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell className={`font-medium text-gray-900 ${cellHighlightClass}`}>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>{new Date(item.date).toLocaleDateString(i18n.language, { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </TableCell>
                  <TableCell className={cellHighlightClass}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {isFullExam ? (
                        <Badge variant="outline" className={`${getCompetenceBadgeClasses("Simulado Completo")} font-semibold shadow-xs`}>
                          {t("history.sectionDisplay.fullSimulated")}
                        </Badge>
                      ) : isWritingV2HistoryRecord(item) ? (
                        <div className="flex flex-col gap-1 shrink-0">
                          <Badge variant="outline" className={`${getCompetenceBadgeClasses("Expression Écrite (EE)")} font-bold`}>
                            Expression Écrite (EE)
                          </Badge>
                          <Badge className="bg-gradient-to-r from-purple-600 to-indigo-500 text-white border-none text-[8px] font-bold px-1.5 py-0 w-fit">
                            {t("history.pedagogicalCorrection")}
                          </Badge>
                        </div>
                      ) : item.isWritingCorrection ? (
                        <div className="flex flex-col gap-1 shrink-0">
                          <Badge variant="outline" className={`${getCompetenceBadgeClasses("Expression Écrite (EE)")} font-bold`}>
                            Expression Écrite (EE)
                          </Badge>
                          <Badge className="bg-gradient-to-r from-purple-600 to-indigo-500 text-white border-none text-[8px] font-bold px-1.5 py-0 w-fit">
                            {t("history.aiCorrection")}
                          </Badge>
                        </div>
                      ) : (
                        <Badge variant="outline" className={getCompetenceBadgeClasses(item.competence)}>
                          {getCompetencyLabel(item.competence)}
                        </Badge>
                      )}
                      
                      {isQueryHighlighted && (
                        <Badge className="bg-purple-600 text-white hover:bg-purple-600 border-none font-bold text-[9px] px-1.5 py-0.5 animate-pulse">
                          {t("history.selected")}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className={`text-xs font-semibold text-gray-600 ${cellHighlightClass}`}>
                    {getSectionDisplay(item, t)}
                  </TableCell>
                  <TableCell className={cellHighlightClass}>
                    {(() => {
                      if (isFullExam) {
                        return (
                          <div className="flex flex-col gap-0.5">
                            <span className="font-bold text-gray-900 text-xs">{t("history.finalScore")}</span>
                            <span className="text-[10px] text-purple-700 font-bold font-mono whitespace-nowrap">{item.score}</span>
                          </div>
                        );
                      }
                      
                      if (isWritingV2HistoryRecord(item)) {
                        const errorCount = item.metadata?.errorCount || 0;
                        const wordCount = item.metadata?.wordCount || 0;
                        const focusAreas = item.metadata?.mainFocusAreas || [];
                        const focusStr = focusAreas.length > 0 ? `Focos: ${focusAreas.join(", ")}` : "";

                        return (
                          <div className="flex flex-col gap-1">
                            <Badge className="bg-purple-50 text-purple-700 hover:bg-purple-100/50 border border-purple-100 font-bold text-[10px] px-1.5 py-0.5 rounded-md w-fit whitespace-nowrap">
                              {t("history.pedagogicalCorrection")}
                            </Badge>
                            <span className="text-[10px] text-gray-500 font-medium font-mono whitespace-nowrap block">
                              {errorCount} {errorCount === 1 ? t("history.errors").split(" ")[0] : t("history.errors")} · {wordCount} {t("history.words")}
                            </span>
                            {focusStr && (
                              <span className="text-[9px] text-purple-700 font-bold max-w-[180px] truncate block" title={focusStr}>
                                {focusStr}
                              </span>
                            )}
                          </div>
                        );
                      }

                      if (item.isWritingCorrection) {
                        return (
                          <div className="flex flex-col gap-0.5">
                            <span className="font-bold text-slate-800 font-mono text-xs">
                              {item.estimatedScore} / {item.maxScore}
                            </span>
                            <span className="text-[10px] text-purple-700 font-bold font-mono">
                              NCLC {item.nclcEstimate} · {item.cecrlEstimate}
                            </span>
                          </div>
                        );
                      }

                      if (item.competence === "Writing" || item.competence === "Expression Écrite (EE)") {
                        const eeScore = resolveWritingScore(item);
                        console.log("[History EE Score Resolved]", { id: item.id, eeScore });
                        
                        if (eeScore === null) {
                          return (
                            <div className="flex flex-col gap-1">
                              <Badge className="bg-yellow-50 border border-yellow-100 text-yellow-700 hover:bg-yellow-100/50 font-bold text-[10px] px-1.5 py-0.5 rounded-md w-fit">
                                {t("history.pendingEe")}
                              </Badge>
                              <span className="text-[11px] text-gray-400 font-medium font-mono block">
                                {(item.words || item.result?.ee?.wordCount || 0)} {t("history.words")}
                              </span>
                            </div>
                          );
                        } else {
                          const eeMaxScore = resolveWritingMaxScore(item);
                          const levels = getWritingNclcFromScore(eeScore);
                          const eeNclc = item.nclc_level || item.metadata?.nclc || levels.nclc;
                          const eeCefr = item.cefr_level || item.metadata?.cefr || levels.cefr;

                          return (
                            <div className="flex flex-col gap-0.5">
                              <span className="font-bold text-slate-800 font-mono text-xs">
                                {eeScore} / {eeMaxScore}
                              </span>
                              <span className="text-[10px] text-purple-700 font-bold font-mono">
                                NCLC {String(eeNclc).replace(/NCLC\s*/i, "")} · {eeCefr}
                              </span>
                            </div>
                          );
                        }
                      }

                      const isListening = item.competence === "Listening";
                      const maxScale = isListening ? 360 : 300;
                      
                      let displayScore = item.score;
                      if (typeof item.score === "string" && !item.score.includes("/")) {
                        displayScore = `${item.score} / ${maxScale}`;
                      } else if (typeof item.score === "number") {
                        displayScore = `${item.score} / ${maxScale}`;
                      } else if (typeof item.score === "string" && item.score.includes("/")) {
                        const parts = item.score.split("/");
                        displayScore = `${parts[0].trim()} / ${maxScale}`;
                      }
                      
                      const levelInfo = getTefLevelByScore(item.competence, item.score);
                      
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-bold text-gray-900 font-mono text-xs">{displayScore}</span>
                          {levelInfo && (
                            <span className="text-[10px] text-primary font-bold font-mono">
                              {levelInfo.label}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className={cellHighlightClass}>
                    <div className="flex items-center gap-2 text-gray-500 font-mono text-sm">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span>{item.timeSpent}</span>
                    </div>
                  </TableCell>
                  <TableCell className={`text-right pr-6 ${cellHighlightClass}`}>
                    <div className="flex items-center justify-end gap-2">
                      {item.isWritingCorrection ? (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setViewingCorrection(item)} 
                          className="text-xs text-purple-700 font-bold hover:text-purple-800 hover:bg-purple-50/80 rounded-lg h-8 flex items-center gap-1 shrink-0"
                        >
                          <Sparkles className="w-3.5 h-3.5 text-purple-500 animate-pulse shrink-0" />
                          {t("history.viewAi")}
                        </Button>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs text-primary font-bold hover:text-primary hover:bg-primary/5 rounded-lg h-8 shrink-0"
                          onClick={() => {
                            if (isWritingV2HistoryRecord(item)) {
                              navigate("/test/result", {
                                state: {
                                  fromHistory: true,
                                  resultType: "writing_v2",
                                  writingVersion: 2,
                                  testType: item.metadata?.testMode || "writing_v2_single",
                                  competency: "writing",
                                  historyRecord: item,
                                  writingTask: item.metadata?.writingTask,
                                  writingCorrection: item.metadata?.writingCorrection,
                                  writingTasks: item.metadata?.writingTasks || (item.metadata?.writingTask ? [item.metadata.writingTask] : [])
                                },
                              });
                              return;
                            }

                            if (isWritingHistoryRecord(item)) {
                              console.log("[HISTORY_REVIEW_EE_RECORD]", JSON.stringify(item, null, 2));

                              navigate("/test/result", {
                                state: {
                                  fromHistory: true,
                                  resultType: "writing",
                                  competency: "EE",
                                  historyRecord: item,
                                  result: item,

                                  writingCorrection:
                                    item?.metadata?.writingCorrection || null,

                                  writingTasks:
                                    item?.metadata?.writingTasks || [],

                                  sectionScores:
                                    item?.metadata?.sectionScores || null,

                                  score:
                                    item?.score ??
                                    item?.metadata?.totalScore ??
                                    item?.metadata?.writingCorrection?.totalScore ??
                                    item?.metadata?.writingCorrection?.score ??
                                    null,

                                  maxScore:
                                    item?.max_score ??
                                    item?.metadata?.maxScore ??
                                    item?.metadata?.writingCorrection?.maxScore ??
                                    450,

                                  nclc:
                                    item?.nclc_level ??
                                    item?.metadata?.nclc ??
                                    item?.metadata?.writingCorrection?.nclc ??
                                    null,

                                  cefr:
                                    item?.cefr_level ??
                                    item?.metadata?.cefr ??
                                    item?.metadata?.writingCorrection?.cefr ??
                                    null,
                                },
                              });

                              return;
                            }

                            if (isWritingV2HistoryRecord(item)) {
                              navigate(`/test/result`, {
                                state: {
                                  fromHistory: true,
                                  resultType: "writing_v2",
                                  writingVersion: 2,
                                  testType: "writing_v2_history_review",
                                  historyRecord: item,
                                  writingTask: item.metadata?.writingTask,
                                  writingCorrection: item.metadata?.writingCorrection,
                                  writingTasks: item.metadata?.writingTask ? [item.metadata.writingTask] : []
                                }
                              });
                              return;
                            }

                            // Current flow for CE/CO or non-writing
                            const url = isFullExam 
                              ? `/test/result?mode=full_exam&id=${item.id}` 
                              : (item.id && !item.isWritingCorrection 
                                ? `/test/result?type=${item.competence.toLowerCase()}&id=${item.id}` 
                                : `/test/result?type=${item.competence.toLowerCase()}&words=${item.words || 0}&text=${encodeURIComponent(item.text || "")}`);
                            
                            navigate(url, {
                              state: {
                                fromHistory: true,
                                resultType: (item.competence === "Writing" || item.competence === "Expression Écrite (EE)") ? "writing" : undefined,
                                historyRecord: item,
                                result: item,
                                writingCorrection: item.metadata?.writingCorrection,
                                writingTasks: item.metadata?.writingTasks,
                                sectionScores: item.metadata?.sectionScores,
                              }
                            });
                          }}
                        >
                          {t("history.review")}
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setItemToDelete(item)}
                        className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* SINGLE ITEM DELETE CONFIRMATION MODAL */}
      {itemToDelete && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200">
          <Card className="max-w-md w-full bg-white border-none shadow-2xl rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <CardHeader className="bg-red-50 pb-4 border-b border-red-100/50">
              <CardTitle className="text-red-800 font-bold flex items-center gap-2 text-base">
                <AlertCircle className="w-5 h-5 text-red-600" />
                {t("history.deleteTitle")}
              </CardTitle>
              <CardDescription className="text-red-600 text-xs font-semibold">{t("history.irreversible")}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-3.5">
              <p className="text-sm text-gray-600 leading-relaxed font-medium">
                {t("history.deleteDesc")}
              </p>
              <div className="p-3 bg-gray-50 rounded-xl text-xs space-y-1.5 text-gray-500 font-semibold border border-gray-100">
                <div>{t("questionsBank.competence")}: <span className="text-gray-800 font-bold">{itemToDelete.competence}</span></div>
                <div>{t("questionsBank.section")}: <span className="text-gray-800 font-bold">{getSectionDisplay(itemToDelete, t)}</span></div>
                <div>{t("history.date")}: <span className="text-gray-800 font-bold">{new Date(itemToDelete.date).toLocaleString(i18n.language, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span></div>
              </div>
            </CardContent>
            <CardFooter className="flex gap-2.5 justify-end pb-6 pt-4 px-6 border-t border-gray-50 bg-gray-50/50">
              <Button 
                variant="outline" 
                className="rounded-xl font-bold text-xs px-4" 
                onClick={() => setItemToDelete(null)}
              >
                {t("common.cancel")}
              </Button>
              <Button 
                variant="destructive" 
                className="rounded-xl font-bold text-xs bg-red-600 hover:bg-red-700 px-4" 
                onClick={handleConfirmSingleDelete}
              >
                {t("questionsBank.delete")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* BATCH DELETE CONFIRMATION MODAL */}
      {isBatchDeleting && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200">
          <Card className="max-w-md w-full bg-white border-none shadow-2xl rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <CardHeader className="bg-red-50 pb-4 border-b border-red-100/50">
              <CardTitle className="text-red-800 font-bold flex items-center gap-2 text-base">
                <AlertCircle className="w-5 h-5 text-red-600" />
                {t("history.batchDeleteTitle")}
              </CardTitle>
              <CardDescription className="text-red-600 text-xs font-semibold">{t("history.batchDeleteSubtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 leading-relaxed font-medium">
                {t("history.batchDeleteDesc", { count: selectedHistoryIds.length })}
              </p>
              <p className="text-xs text-gray-400 mt-2.5 leading-relaxed">
                {t("history.batchDeleteDisclaimer")}
              </p>
            </CardContent>
            <CardFooter className="flex gap-2.5 justify-end pb-6 pt-4 px-6 border-t border-gray-50 bg-gray-50/50">
              <Button 
                variant="outline" 
                className="rounded-xl font-bold text-xs px-4" 
                onClick={() => setIsBatchDeleting(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button 
                variant="destructive" 
                className="rounded-xl font-bold text-xs bg-red-600 hover:bg-red-700 px-4" 
                onClick={handleConfirmBatchDelete}
              >
                {t("questionsBank.delete")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* DETAILED ATTEMPT SUMMARY MODAL */}
      <ContextModal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title={t("history.detailsTitle")}
        description={t("history.detailsDesc")}
        size="md"
      >
        {selectedItem && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">{t("history.testDate")}</span>
              <span className="text-xs font-semibold text-gray-700">
                {new Date(selectedItem.date).toLocaleDateString(i18n.language, { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">{t("questionsBank.competence")}</span>
              {selectedItem.mode === "full_exam" ? (
                <Badge className="bg-purple-600 text-white border-none font-semibold text-[10px]">{t("history.sectionDisplay.fullSimulated")}</Badge>
              ) : (
                <Badge variant="outline" className={
                  selectedItem.competence === "Reading" ? "border-purple-200 text-purple-700 bg-purple-50" :
                  selectedItem.competence === "Listening" ? "border-blue-200 text-blue-700 bg-blue-50" :
                  "border-emerald-200 text-emerald-700 bg-emerald-50"
                }>
                  {selectedItem.competence}
                </Badge>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">{t("history.sectionTask")}</span>
              <span className="text-xs font-semibold text-gray-700">{getSectionDisplay(selectedItem, t)}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">{t("history.timeSpentCol")}</span>
              <span className="text-xs font-bold text-gray-700 font-mono">{selectedItem.timeSpent}</span>
            </div>

            <div className="pt-4 border-t border-gray-50 flex flex-col items-center justify-center p-4 bg-gray-50 rounded-xl">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">{t("history.finalScore")}</span>
              <div className="text-3xl font-black text-primary font-mono text-center">{formatCompactScore(selectedItem.score)}</div>
              {selectedItem.words !== undefined && (
                <span className="text-xs text-gray-400 font-medium font-mono mt-1">{t("history.wordsWritten", { count: selectedItem.words })}</span>
              )}
            </div>

            <div className="pt-4 border-t border-gray-100 flex gap-2 justify-end">
              <Button 
                variant="outline"
                onClick={() => setSelectedItem(null)}
                className="rounded-xl border-gray-100 font-semibold text-xs"
              >
                {t("history.close")}
              </Button>
              <Button 
                onClick={() => {
                  const queryParams = selectedItem.mode === "full_exam" 
                    ? `mode=full_exam&id=${selectedItem.id}` 
                    : `type=${selectedItem.competence.toLowerCase()}&words=${selectedItem.words || 0}&text=${encodeURIComponent(selectedItem.text || "")}&id=${selectedItem.id}`;
                  
                  navigate(`/test/result?${queryParams}`, {
                    state: {
                      historyRecord: selectedItem,
                      result: selectedItem,
                      writingCorrection: selectedItem.metadata?.writingCorrection,
                      fromHistory: true
                    }
                  });
                }}
                className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs"
              >
                {t("history.viewFullReview")}
              </Button>
            </div>
          </div>
        )}
      </ContextModal>

      {/* DETAILED WRITING CORRECTION REPORT IA MODAL */}
      <ContextModal
        isOpen={!!viewingCorrection}
        onClose={() => setViewingCorrection(null)}
        title={t("history.iaReportTitle")}
        description={t("history.iaReportDesc")}
        size="7xl"
      >
        {viewingCorrection && (
          <div className="space-y-4">
            <WritingCorrectionReport 
              correction={viewingCorrection} 
              showToast={(msg) => showToast(msg)} 
              readOnly={true} 
            />

            {/* Collapsible Technical Info Block */}
            <div className="mt-4 border border-slate-100 rounded-2xl overflow-hidden bg-slate-50/50">
              <details className="group">
                <summary className="flex items-center justify-between p-4 cursor-pointer select-none">
                  <div className="flex items-center gap-2 text-xs font-extrabold text-slate-500 uppercase tracking-wider">
                    <Info className="w-4 h-4 text-slate-400" />
                    {t("history.techMetadata")}
                  </div>
                  <span className="text-xs font-bold text-purple-600 group-open:hidden">{t("history.expandData")}</span>
                  <span className="text-xs font-bold text-slate-400 hidden group-open:inline">{t("history.collapseData")}</span>
                </summary>
                
                <div className="p-4 pt-0 border-t border-slate-100 text-xs space-y-3 font-semibold text-slate-600 animate-in slide-in-from-top-1 duration-150">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 bg-white rounded-xl border border-slate-100 space-y-1">
                      <span className="text-[10px] text-gray-400 font-extrabold uppercase">{t("history.correctionId")}</span>
                      <span className="font-mono text-gray-800 font-bold block select-all">{viewingCorrection.correctionId || viewingCorrection.id}</span>
                    </div>
                    <div className="p-3 bg-white rounded-xl border border-slate-100 space-y-1">
                      <span className="text-[10px] text-gray-400 font-extrabold uppercase">{t("history.correctionSource")}</span>
                      <span className="font-bold text-purple-700 block">{viewingCorrection.source === "openai" ? "OpenAI GPT-4" : (viewingCorrection.source === "mock_fallback" ? "IA Simulada (Mock)" : viewingCorrection.source || "Simulação Local")}</span>
                    </div>
                    <div className="p-3 bg-white rounded-xl border border-slate-100 space-y-1">
                      <span className="text-[10px] text-gray-400 font-extrabold uppercase">{t("history.promptVersion")}</span>
                      <span className="font-mono text-gray-800 font-bold block">{viewingCorrection.promptVersion || "ee-correction-prompt-v1"}</span>
                    </div>
                    <div className="p-3 bg-white rounded-xl border border-slate-100 space-y-1">
                      <span className="text-[10px] text-gray-400 font-extrabold uppercase">{t("history.rubricVersion")}</span>
                      <span className="font-mono text-gray-800 font-bold block">{viewingCorrection.rubricVersion || "ee-rubric-v1"}</span>
                    </div>
                    <div className="p-3 bg-white rounded-xl border border-slate-100 space-y-1">
                      <span className="text-[10px] text-gray-400 font-extrabold uppercase">{t("history.correctionMode")}</span>
                      <span className="font-mono text-gray-800 font-bold block">{viewingCorrection.correctionMode || "tef_complete"}</span>
                    </div>
                    <div className="p-3 bg-white rounded-xl border border-slate-100 space-y-1">
                      <span className="text-[10px] text-gray-400 font-extrabold uppercase">{t("history.confidence")}</span>
                      <span className="font-bold text-gray-800 block capitalize">{viewingCorrection.scoreConfidence || "medium"}</span>
                    </div>
                  </div>
                  {viewingCorrection.scoreJustification && (
                    <div className="p-3 bg-white rounded-xl border border-slate-100 space-y-1">
                      <span className="text-[10px] text-gray-400 font-extrabold uppercase block">{t("history.confidenceJustification")}</span>
                      <p className="text-[11px] text-slate-700 leading-relaxed font-medium">{viewingCorrection.scoreJustification}</p>
                    </div>
                  )}
                </div>
              </details>
            </div>
          </div>
        )}
      </ContextModal>

      {/* Floating Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 right-6 z-50 bg-gray-950 text-white font-bold text-xs py-3.5 px-6 rounded-2xl shadow-xl flex items-center gap-2 animate-in slide-in-from-top duration-300">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-ping" />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
}
